import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// resolveRichText substitutes embedded {{field}} tokens the same way
// <RichTextContent> does. We call it explicitly because the pet-policy blurb is
// post-processed into a bullet list (petPolicyToListHtml) before rendering.
import { resolveRichText } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
// Shared with the Header's Community Info pop-up so the page and the pop-up
// render an identical square-bullet pet-policy list.
import {
  hasValue,
  petPolicyToListHtml,
  buildStructuredPetPolicyHtml,
  fieldStr,
  isAffirmative,
} from '../lib/petPolicyList';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Amenities CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Amenities',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Single pet-policy image shown where the stat tiles used to sit. Bound to the
// first gallery photo so it always has a sensible default; the editor can swap
// it (and its alt text) to any gallery item in one action via sourceBinding.
const petImageDef: SlotGroupDefinition = {
  label: 'Pet Policy Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);
  const petImage = useSlotGroup('petpolicy.image', petImageDef);

  // Pet-policy content rendered as a square-bullet list, mirroring Community
  // Info. Primary source is property_pet_policy_description (token override
  // wins); falls back to structured pet-policy fields when empty / not a list.
  const property = useProperty() as Record<string, unknown>;
  const petPolicyBlurb = useContentToken('petpolicy.blurb', {
    defaultField: 'property_pet_policy_description',
  });
  const petPolicyBlurbHtml = petPolicyToListHtml(
    petPolicyBlurb ? resolveRichText(petPolicyBlurb, property as Record<string, any>) : '',
  );
  const structuredPetPolicyHtml = buildStructuredPetPolicyHtml({
    cats: isAffirmative(usePropertyField('property_allows_cats')),
    dogs: isAffirmative(usePropertyField('property_allows_dogs')),
    maxPets: fieldStr(usePropertyField('property_max_pets')),
    maxWeight: fieldStr(usePropertyField('property_max_pet_weight_limit')),
    monthlyRent: fieldStr(usePropertyField('property_monthly_pet_rent')),
    fee: fieldStr(usePropertyField('property_upfront_pet_fee')),
    deposit: fieldStr(usePropertyField('property_pet_deposit')),
    restrictions: fieldStr(usePropertyField('property_pet_restrictions')),
  });
  const petPolicyListHtml = /<li[\s>]/i.test(petPolicyBlurbHtml)
    ? petPolicyBlurbHtml
    : structuredPetPolicyHtml || petPolicyBlurbHtml;

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up pt-4 md:pt-8 pb-4 md:pb-8 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: pet policy details as a square-bullet list */}
          <div className="pet-content">
            {/* Section title */}
            <RichTextContent path="petpolicy.title" as="h2" className="pet-title" />

            {/* Pet policy — rendered as a bullet list (see petPolicyToListHtml)
                so it reads as discrete items instead of a text block.
                EditableToken keeps the blurb editable in place; the resolved
                list is injected as its child, mirroring the Community Info
                pop-up. */}
            {hasValue(petPolicyListHtml) && (
              <EditableToken
                path="petpolicy.blurb"
                type="richtext"
                label="Pet Policy"
                defaultField="property_pet_policy_description"
                as="div"
                className="pet-policy-list [&_a]:underline"
              >
                <span
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: petPolicyListHtml }}
                  style={{ display: 'contents' }}
                />
              </EditableToken>
            )}

            {/* CTA — sized like the Floor Plans fee-guide button: inset to 88%
                of the column on phones (centred, so the inset is symmetric),
                then content-width and left-aligned with the copy from sm up. */}
            <div className="pet-cta-wrap mt-8 flex justify-center sm:justify-start">
              <EditableSlotGroup
                groupPath="petpolicy.cta_link"
                definition={ctaLinkDef}
                className="w-[88%] sm:w-auto"
              >
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  {ctaLink.label || 'View Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: image (where the stat tiles used to sit) */}
          <div className="pet-image-col">
            <EditableSlotGroup groupPath="petpolicy.image" definition={petImageDef} className="pet-image-wrap">
              {petImage.image ? (
                <VibesitesImage
                  url={petImage.image}
                  alt={petImage.alt || ''}
                  sizes="(min-width: 768px) 50vw, 100vw"
                  className="pet-image"
                />
              ) : (
                <div className="pet-image pet-image--placeholder" />
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Single column on phones, stacked title → blurb → CTA → image.
           .pet-content dissolves into the flex container so the image can be
           ordered after its siblings instead of above the whole text block. */
        .pet-layout {
          display: flex;
          flex-direction: column;
        }
        .pet-content { display: contents; }
        .pet-title { order: 1; }
        .pet-policy-list { order: 2; }
        .pet-cta-wrap { order: 3; }
        .pet-image-col {
          order: 4;
          margin-top: 2.5rem;
        }

        @media (min-width: 768px) {
          .pet-layout {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: center;
          }
          .pet-content {
            display: block;
            order: 1;
          }
          .pet-image-col {
            order: 2;
            margin-top: 0;
          }
        }

        .pet-image-wrap {
          display: block;
          width: 100%;
          overflow: hidden;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .pet-image {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 260px;
          max-height: 420px;
          object-fit: cover;
          object-position: center;
        }

        .pet-image--placeholder {
          background: var(--vibe-color-surface, #f7f7f8);
        }

        .pet-title {
          font-family: var(--vibe-font-body);
          font-size: 1.75rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          margin-bottom: 1.5rem;
        }

        /* Square-bullet pet-policy list, matching the Community Info pop-up.
           All text renders at normal weight — no bolding. */
        .pet-policy-list {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        .pet-policy-list p {
          margin: 0 0 0.5rem;
        }
        .pet-policy-list p:last-child { margin-bottom: 0; }
        /* Render the blurb's structured lists as discrete bullet items instead
           of a collapsed run of lines (Tailwind Preflight strips list markers,
           so they must be re-declared here). */
        .pet-policy-list ul,
        .pet-policy-list ol {
          list-style: square;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .pet-policy-list ol { list-style: decimal; }
        .pet-policy-list ul:last-child,
        .pet-policy-list ol:last-child { margin-bottom: 0; }
        .pet-policy-list li {
          margin: 0 0 0.35rem;
        }
        .pet-policy-list li:last-child { margin-bottom: 0; }
        /* Square bullets (unordered lists only) drawn without inflating each
           row's line height: suppress the native marker and draw a sized
           square via an absolutely-positioned ::before. Sized to match
           .amenities-col-item::before in AmenitiesList. Ordered lists keep
           their numbers. */
        .pet-policy-list ul {
          list-style: none;
        }
        .pet-policy-list ul li {
          position: relative;
        }
        .pet-policy-list ul li::before {
          content: "";
          position: absolute;
          left: -1rem;
          top: 0.8em;
          transform: translateY(-50%);
          width: 0.325em;
          height: 0.325em;
          background: currentColor;
        }
        /* Section sub-headings inside the blurb ("Application & Admin Fees",
           label lines, etc.). Kept at normal weight so nothing is bold. */
        .pet-policy-list h3,
        .pet-policy-list h4 {
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 400;
          color: var(--vibe-color-text);
          margin: 1rem 0 0.4rem;
        }
        .pet-policy-list h3:first-child,
        .pet-policy-list h4:first-child { margin-top: 0; }
        /* Inline emphasis rendered at normal weight — no bolding. */
        .pet-policy-list strong,
        .pet-policy-list b {
          font-weight: 400;
        }
        .pet-policy-list ul + p,
        .pet-policy-list ol + p { margin-top: 0.75rem; }

        /* Width mirrors the Floor Plans fee-guide button: fills the 88%-wide
           wrapper on phones, then content-width (1.5rem side padding) from the
           same 640px breakpoint up. */
        .pet-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.1em;
          color: #fff;
          background: #000;
          border: 1px solid #000;
          padding: 1rem 1.5rem;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }
        @media (min-width: 640px) {
          .pet-cta {
            width: auto;
          }
        }
        .pet-cta:hover {
          background: #fff;
          color: #000;
        }

        /* Tablet: same rebalance as PropertyOverview / FloorplansList, adapted
           to this layout. There is no inset padding to reclaim here and the
           list already sits at 1.6 leading, so the gutter is the only shared
           lever; the rest goes to the photo, which otherwise bottoms out near
           its 260px floor beside a much taller policy list. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .pet-layout {
            gap: 2rem;
            align-items: stretch;
          }
          /* Preserve today's centring for short policies, where the photo —
             not the text — sets the row height. */
          .pet-content { align-self: center; }
          /* Let the photo take the row's height (object-fit crops it). The
             wrapper carries the same 420px ceiling as the image so the
             shadowed box can never outgrow the photo inside it. */
          .pet-image-col {
            display: flex;
            align-items: center;
          }
          .pet-image-wrap {
            height: 100%;
            max-height: 420px;
          }
          .pet-image {
            height: 100%;
            min-height: 0;
            max-height: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.title': {
      label: 'Section Title',
      type: 'richtext',
      description: 'Heading shown above the pet policy list.',
      defaultValue: '<p>Pet Policy</p>',
    },
    'petpolicy.blurb': {
      label: 'Pet Policy',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      description:
        'Pet policy content, rendered as a square-bullet list (same Multihub field as Community Info). Override from the live Duda /pets page when that copy differs. Falls back to structured pet-policy fields when empty.',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': {
      label: 'View Amenities CTA',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/amenities',
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Amenities',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'petpolicy.image': {
      label: 'Pet Policy Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PetPolicy as any).contentSchema = contentSchema;

export default PetPolicy;
